import React, { useEffect, useMemo, useState } from 'react'
import { message, Pagination, Select } from 'antd'
import { Course } from '../../../api/courses'
import { getUsers, User } from '../../../api/user'

const statusOptions = [
  { label: 'Paid', value: 'paid' },
  { label: 'Pending', value: 'pending' },
  { label: 'Cancelled', value: 'cancelled' },
]

const contentType = [
  { label: 'Course', value: 'Course' },
  { label: 'CBT', value: 'CBT' },
  { label: 'Live Teaching', value: 'Live_Teaching' },
];

export interface CreateTransactionPayload {
  order_id: string
  course_id: number
  course_ids: number[]
  email: string
  fullname: string
  users: Array<{ email: string; fullname: string }>
  total: number
  status: string
  notes?: string
  type: string
}

interface CreateTransactionModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: (data: CreateTransactionPayload) => void
  courses: Course[]
  loading?: boolean,
  setSelectedType: any,
  selectedType: string
}

const CreateTransactionModal: React.FC<CreateTransactionModalProps> = ({
  visible,
  onClose,
  onSubmit,
  courses,
  loading,
  setSelectedType,
  selectedType
}) => {
  const [form, setForm] = useState({
    order_id: '',
    course_id: 0,
    course_ids: [] as number[],
    email: '',
    fullname: '',
    users: [] as Array<{ email: string; fullname: string }>,
    user_ids: [] as number[],
    total: 0,
    status: '',
    notes: '', // tambahkan notes
    type: 'manual', // tambahkan type
  })
  const [userOptions, setUserOptions] = useState<User[]>([])
  const [selectedUsers, setSelectedUsers] = useState<User[]>([])
  const [userSearch, setUserSearch] = useState('')
  const [userLoading, setUserLoading] = useState(false)
  const [courseSearch, setCourseSearch] = useState('')
  const [coursePage, setCoursePage] = useState(1)
  const coursePageSize = 10

  const filteredCourses = useMemo(() => {
    if (!courseSearch) {
      return courses
    }
    if (courseSearch.length < 3) {
      return []
    }

    const term = courseSearch.toLowerCase()
    return courses.filter((course) =>
      course.title.toLowerCase().includes(term),
    )
  }, [courses, courseSearch])

  const pagedCourses = useMemo(() => {
    const start = (coursePage - 1) * coursePageSize
    return filteredCourses.slice(start, start + coursePageSize)
  }, [coursePage, coursePageSize, filteredCourses])

  const selectedCourseOptions = useMemo(() => {
    if (!form.course_ids.length) {
      return []
    }
    return courses
      .filter((course) => form.course_ids.includes(course.id))
      .map((course) => ({ label: course.title, value: course.id }))
  }, [courses, form.course_ids])

  const courseOptions = useMemo(() => {
    const options = pagedCourses.map((course) => ({
      label: course.title,
      value: course.id,
    }))
    if (!selectedCourseOptions.length) {
      return options
    }
    const selectedIds = new Set(selectedCourseOptions.map((opt) => opt.value))
    const merged = [...selectedCourseOptions]
    options.forEach((opt) => {
      if (!selectedIds.has(opt.value)) {
        merged.push(opt)
      }
    })
    return merged
  }, [pagedCourses, selectedCourseOptions])

  useEffect(() => {
    if (visible) {
      setForm({
        order_id: '',
        course_id: 0,
        course_ids: [],
        email: '',
        fullname: '',
        users: [],
        user_ids: [],
        total: 0,
        status: '',
        notes: '', // reset notes
        type: 'manual', // reset type
      })
      setSelectedUsers([])
      setUserOptions([])
      setUserSearch('')
      setCourseSearch('')
      setCoursePage(1)
    }
  }, [visible])

  useEffect(() => {
    setCoursePage(1)
  }, [courseSearch, courses])

  useEffect(() => {
    if (!visible) {
      return
    }

    const delay = setTimeout(async () => {
      setUserLoading(true)
      try {
        const res = await getUsers({ search: userSearch }, true)
        setUserOptions(res.data || [])
      } catch {
        setUserOptions([])
      } finally {
        setUserLoading(false)
      }
    }, 400)

    return () => clearTimeout(delay)
  }, [userSearch, visible])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSelectChange = (name: string, value: any) => {
    setForm({ ...form, [name]: value })
  }

  const handleCourseSelect = (value: number[]) => {
    handleSelectChange('course_ids', value)
    setCourseSearch('')
    setCoursePage(1)
  }

  const handleUserSelectChange = (value: number[]) => {
    const mergedUsers = [...selectedUsers, ...userOptions]
    const nextUsers = value
      .map((id) => mergedUsers.find((user) => user.id === id))
      .filter(Boolean) as User[]

    setSelectedUsers(nextUsers)
    setForm({ ...form, user_ids: value })
  }

  const handleContentTypeChange = (value: string) => {
    setSelectedType(value)
    setForm((prev) => ({ ...prev, course_id: 0, course_ids: [] }))
    setCourseSearch('')
    setCoursePage(1)
  }

  const courseSelectValue = form.course_ids.length ? form.course_ids : undefined
  const userSelectValue = form.user_ids.length ? form.user_ids : undefined
  const userOptionsMapped = userOptions.map((user) => ({
    label: `${user.fullname} (${user.email})`,
    value: user.id,
  }))

  const transactionCount = useMemo(() => {
    const userCount = selectedUsers.length || (form.fullname && form.email ? 1 : 0)
    const courseCount = form.course_ids.length || 0
    if (!userCount || !courseCount) {
      return 0
    }
    return userCount * courseCount
  }, [form.course_ids.length, form.email, form.fullname, selectedUsers.length])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.course_ids.length) {
      message.error('Pilih minimal 1 course')
      return
    }
    if (!selectedUsers.length && (!form.fullname || !form.email)) {
      message.error('Isi nama dan email atau pilih user')
      return
    }

    const users = selectedUsers.length
      ? selectedUsers.map((user) => ({
          email: user.email,
          fullname: user.fullname,
        }))
      : [{ email: form.email, fullname: form.fullname }]

    onSubmit({
      ...form,
      course_id: form.course_ids[0] || 0,
      users,
    })
  }

  return (
    <>
      {visible && (
        <div
          className="modal-backdrop fade show"
          style={{ zIndex: 1040 }}
          onClick={onClose}
        />
      )}
      <div
        className={`modal fade${visible ? ' show d-block' : ''}`}
        tabIndex={-1}
        style={{ display: visible ? 'block' : 'none', zIndex: 1050 }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="fw-bold">Add Transaction</h5>
              <button
                type="button"
                className="btn-close custom-btn-close"
                aria-label="Close"
                onClick={onClose}
              >
                <i className="isax isax-close-circle5" />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Order ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="order_id"
                        value={form.order_id}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Tipe Konten <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={contentType.map((c) => ({
                          label: c.label,
                          value: c.value,
                        }))}
                        className="select d-flex"
                        placeholder="Select Course"
                        value={selectedType}
                        onChange={(val: any) => handleContentTypeChange(val)}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Nama <span className="text-danger">*</span>
                      </label>
                      <input
                        type="fullname"
                        className="form-control"
                        name="fullname"
                        value={form.fullname}
                        onChange={handleChange}
                        required
                        disabled={form.user_ids.length > 0}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Email <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        disabled={form.user_ids.length > 0}
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Pilih User (Multi)</label>
                      <Select
                        mode="multiple"
                        options={userOptionsMapped}
                        className="select d-flex"
                        placeholder="Cari user..."
                        value={userSelectValue}
                        onChange={(val: any) => handleUserSelectChange(val)}
                        showSearch
                        filterOption={false}
                        onSearch={(value) => setUserSearch(value)}
                        allowClear={!!userSelectValue}
                        loading={userLoading}
                        onClear={() => {
                          handleUserSelectChange([])
                          setUserSearch('')
                        }}
                        style={{ width: '100%' }}
                      />
                      <small className="text-muted">
                        Jika memilih user, field nama dan email akan diisi otomatis
                      </small>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Course <span className="text-danger">*</span>
                      </label>
                      <Select
                        mode="multiple"
                        options={courseOptions}
                        className="select d-flex"
                        placeholder="Select Course"
                        value={courseSelectValue}
                        onChange={(val: any) => handleCourseSelect(val)}
                        showSearch
                        filterOption={false}
                        onSearch={(value) => setCourseSearch(value)}
                        allowClear={!!courseSelectValue}
                        suffixIcon={courseSelectValue ? null : undefined}
                        onClear={() => {
                          handleSelectChange('course_ids', [])
                          setCourseSearch('')
                          setCoursePage(1)
                        }}
                        notFoundContent={
                          courseSearch && courseSearch.length < 3
                            ? 'Minimal 3 karakter'
                            : 'Tidak ditemukan'
                        }
                        dropdownRender={(menu) => (
                          <div>
                            {menu}
                            <div className="d-flex justify-content-end px-3 py-2">
                              <Pagination
                                size="small"
                                current={coursePage}
                                pageSize={coursePageSize}
                                total={filteredCourses.length}
                                onChange={(page) => setCoursePage(page)}
                                showSizeChanger={false}
                              />
                            </div>
                          </div>
                        )}
                        disabled={selectedType === ''}
                        style={{ width: '100%' }}
                      />
                      {transactionCount > 1 && (
                        <small className="text-muted">
                          Akan dibuat {transactionCount} transaksi. Order ID dipakai sebagai prefix.
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Amount <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        name="total"
                        value={form.total}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <Select
                        options={statusOptions}
                        className="select d-flex"
                        placeholder="Select Status"
                        value={form.status}
                        onChange={(val: any) =>
                          handleSelectChange('status', val)
                        }
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Notes</label>
                      <textarea
                        className="form-control"
                        name="notes"
                        value={form.notes}
                        onChange={handleChange}
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn bg-gray-100 rounded-pill me-2"
                  type="button"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-secondary rounded-pill"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}

export default CreateTransactionModal
